'use client';

import Logo from '@/components/atoms/brand/Logo';
import { Checkbox, Input, Label, PasswordInput } from '@glperp/shared-ui';
import React, { ChangeEvent, FormEvent, useState } from 'react';
import { useRouter } from 'next/navigation';
import axios from 'axios';
import { baseURL } from '@/lib/utils/axiosClient';
import { validateEmail } from '@/lib/utils';
import { setCookie } from 'cookies-next';
import { toast } from 'sonner';
import Link from 'next/link';

interface IUserData {
  email: string;
  password: string;
}

export default function Login() {
  const [formData, setFormData] = useState<IUserData>({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState<IUserData>({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormErrors({ email: '', password: '' });
    const { name } = e.target;
    const { value } = e.target;

    setFormData({ ...formData, [name]: value });
  };

  const router = useRouter();

  // router.refresh();
  // # region: Older logincode
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      setFormErrors({ ...formErrors, email: 'Email is required' });
      return;
    }
    if (!validateEmail(formData.email)) {
      setFormErrors({ ...formErrors, email: 'Invalid email' });
      return;
    }

    if (!formData.password) {
      setFormErrors({ ...formErrors, password: 'Password is required' });
      return;
    }

    setFormErrors({ email: '', password: '' });
    setIsLoading(true);

    try {
      const response = await axios.post(`${baseURL}login/`, {
        email: formData.email,
        password: formData.password
      });

      if (response.status === 200) {
        if (
          response.data.role !== 'Sales Representative' &&
          response.data.role !== 'Sale Representative'
        ) {
          toast.warning('User not a Sales Representative');
          return;
        }
        setCookie('token', response.data.token);
        setCookie('userId', response.data.id);
        localStorage.setItem('user', JSON.stringify(response.data));
        toast.success('Login Successful! Redirecting to Dashboard...');
        router.push('/');
      } else {
        const errorData = response.data;
        setError(errorData.message ?? 'Invalid email or password');
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        const { status, data } = err.response;
        if (status === 403 && data?.code === 'PASSWORD_CHANGE_REQUIRED') {
          toast.error(
            'Password change required. Redirecting to reset password page...'
          );
          router.push('/reset-password');
          return;
        }
        console.error('Login error:', err.message);
        setError(
          data?.message ?? 'Invalid email or password. Please try again.'
        );
      } else if (err instanceof Error) {
        console.error('Login error:', err.message);
        setError('Invalid email or password. Please try again.');
      } else {
        console.error('Login error:', err);
        setError('Invalid email or password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col justify-center h-full gap-10 -mt-16 p-4">
      <div className="flex flex-col gap-2">
        <h2 className="font-bold text-xl">Welcome to</h2>
        <Logo />
        <h2 className="font-bold text-end text-xl">Sales Portal</h2>
      </div>
      <div className="flex flex-col gap-4">
        <form onSubmit={handleLogin} className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            {error && (
              <p className="text-sm font-semibold text-red-500">{error}</p>
            )}
            <Label variant="field" htmlFor="email">
              Email Address
            </Label>
            {formErrors.email && (
              <p id="email-error" className="text-xs text-red-500">
                {formErrors.email}
              </p>
            )}
            <Input
              inputSize="lg"
              type="email"
              id="email"
              name="email"
              autoComplete="on"
              onChange={handleChange}
              aria-invalid={formErrors.email ? true : undefined}
              aria-describedby={formErrors.email ? 'email-error' : undefined}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label variant="field" htmlFor="password">
              Password
            </Label>
            {formErrors.password && (
              <p id="password-error" className="text-xs text-red-500">
                {formErrors.password}
              </p>
            )}
            <PasswordInput
              inputSize="lg"
              name="password"
              id="password"
              autoComplete="current-password"
              onChange={handleChange}
              aria-invalid={formErrors.password ? true : undefined}
              aria-describedby={
                formErrors.password ? 'password-error' : undefined
              }
            />
            <div className="flex flex-wrap justify-between gap-2">
              <div className="flex items-center gap-2">
                <Checkbox id="agree-terms" className="h-4 w-4" />
                <Label htmlFor="agree-terms" className="font-normal">
                  Agree to Terms and Conditions
                </Label>
              </div>
              <div>
                <Link href="/reset-password" className="font-medium text-sm">
                  Forgot Password
                </Link>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              type="submit"
              className="w-2/3 bg-slate-800 rounded-xl h-10 text-white font-semibold"
              style={{ opacity: isLoading ? 0.5 : 1 }}
            >
              {isLoading ? 'Signing in...' : 'Login'}
            </button>
          </div>
        </form>
        <div className="flex justify-center gap-5">
          <p className="text-sm">Don&apos;t have an account?</p>
          <a className="text-sm font-bold" href="https://globallinkplus.com/">
            Request Demo
          </a>
        </div>
      </div>
    </div>
  );
}
